import * as React from "react";
import { graphql, PageProps } from "gatsby";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

import { DEFAULT_LANGUAGE } from "@config/consts";

import { AVAILABLE_COUNTRIES, ORIGIN, SERVICE_TYPE } from "@typings/globals";

import theme from "@ui/themes/default";

import { langFromTag } from "@services/LangFromTag/LangFromTag";

import { useBreakpoint } from "@hooks/useBreakpoint";

import UTMCatcher from "../components/UTMCatcher";
import Layout from "../components/Layout";
import SEO, { SeoProps } from "../components/SEO";

import {
  HeroSection,
  HeroProps,
  SliceHeroProps,
  CitiesServices,
  CitiesServicesProps,
  Offer,
  OfferProps,
  CleaningTipsProps,
  QuoteSection,
  QuoteSectionProps,
  BecomeBatmaidSection,
  BecomeBatmaidProps,
  VideoSection,
  VideoProps,
  HowItWorks,
  HowItWorksProps,
  Partners,
  PartnersProps,
  OurServicesProps,
  OurServices,
  TextSection,
  TextSectionProps,
} from "./Homepage/";

// workaround for masive text replacement
// https://batmaid.myjetbrains.com/youtrack/issue/OFF-1780/Replace-the-outdated-USPs-on-the-city-pages
const blacklistedTexts: Record<string, string> = {
  "Non-binding contract": "Same cleaning agent every time",
  "Unverbindlicher Vertrag": "Immer dieselbe Reinigungskraft",
  "Contrat sans engagement": "Toujours le même agent de ménage",
  "Contratto non vincolante": "Sempre lo stesso addetto alle pulizie",
  "Free cancellation up to 48h before your cleaning":
    "Mobile App and user-friendly online platform",
  "Kostenlose Stornierung bis zu 48h vor Ihrer Reinigung":
    "Mobile App und benutzerfreundliche Online-Plattform",
  "Annulation gratuite jusqu'à 48h avant votre ménage":
    "Application mobile et plateforme en ligne facile d'utilisation",
  "Cancellazione gratuita fino a 48 ore prima della pulizia":
    "App mobile e piattaforma online facile da usare",
};

interface PageData
  extends Omit<HeroProps, "language">,
    CitiesServicesProps,
    QuoteSectionProps,
    VideoProps,
    BecomeBatmaidProps,
    HowItWorksProps,
    CleaningTipsProps,
    SeoProps,
    PartnersProps,
    TextSectionProps,
    OfferProps,
    OurServicesProps,
    SeoProps {
  title: {
    text: string;
  };
  slug: {
    text: string;
  };
  body: SliceHeroProps[];
}

type Props = PageProps<
  { prismicCityPage: { data: PageData } },
  {
    lang: string;
    origin: ORIGIN;
    country: AVAILABLE_COUNTRIES;
  }
>;

const CityPage = (props: Props): React.ReactElement => {
  const [serviceType, setServiceType] = React.useState<SERVICE_TYPE>(
    SERVICE_TYPE.HOME_CLEANING,
  );

  const { data, pageContext } = props;
  const { prismicCityPage } = data;

  const { desktop, desktopLarge } = useBreakpoint("v1");

  const language = langFromTag(pageContext.lang) || DEFAULT_LANGUAGE;

  const heroesList: typeof prismicCityPage.data.body = (() => {
    if (props.pageContext.country !== AVAILABLE_COUNTRIES.CH) {
      return prismicCityPage.data.body;
    }

    const result = [];

    for (let list of prismicCityPage.data.body) {
      const { primary } = list;
      const items = list.items.map(item => {
        const trimmedText = item.list_item.text.trim();

        if (Object.keys(blacklistedTexts).includes(trimmedText)) {
          item.list_item.text = blacklistedTexts[trimmedText];
        }

        return item;
      });

      result.push({
        primary,
        items,
      });
    }

    return result;
  })();

  const offerCards: typeof prismicCityPage.data.what_do_we_offer_cards =
    (() => {
      if (props.pageContext.country !== AVAILABLE_COUNTRIES.CH) {
        return prismicCityPage.data.what_do_we_offer_cards;
      }

      const result = [];

      for (let card of prismicCityPage.data.what_do_we_offer_cards) {
        const {
          what_do_we_offer_card_image,
          what_do_we_offer_card_image_mobile,
          what_do_we_offer_card_title,
          ...elements
        } = card;

        Object.keys(elements).map((key: keyof typeof elements) => {
          if (
            Object.keys(blacklistedTexts).includes(elements[key].text?.trim())
          ) {
            elements[key].text = blacklistedTexts[elements[key].text];
          }
        });

        result.push({
          what_do_we_offer_card_image,
          what_do_we_offer_card_image_mobile,
          what_do_we_offer_card_title,
          ...elements,
        });
      }

      return result;
    })();

  const heroData: HeroProps = {
    language,
    hero_image_mobile: prismicCityPage.data.hero_image_mobile,
    booking_form_submit: prismicCityPage.data.booking_form_submit,
    booking_form_placeholder: prismicCityPage.data.booking_form_placeholder,
    email_submit: prismicCityPage.data.email_submit,
    email_placeholder: prismicCityPage.data.email_placeholder,
    heroesList,
    landingUrl: `/${language}`,
    hero_breadcrumb_text: prismicCityPage.data.hero_breadcrumb_text,
  };

  const enjoyServiceData: CitiesServicesProps = {
    enjoy_service_title: prismicCityPage.data.enjoy_service_title,
    enjoy_service_cities: prismicCityPage.data.enjoy_service_cities,
    language,
  };

  const howItWorksData: HowItWorksProps = {
    how_it_works_title: prismicCityPage.data.how_it_works_title,
    how_it_works_title_mobile: prismicCityPage.data.how_it_works_title_mobile,
    how_it_works_step_one: prismicCityPage.data.how_it_works_step_one,
    how_it_works_step_one_icon: prismicCityPage.data.how_it_works_step_one_icon,
    how_it_works_step_two: prismicCityPage.data.how_it_works_step_two,
    how_it_works_step_two_icon: prismicCityPage.data.how_it_works_step_two_icon,
    how_it_works_step_three: prismicCityPage.data.how_it_works_step_three,
    how_it_works_step_three_icon:
      prismicCityPage.data.how_it_works_step_three_icon,
    iconsSecondaryColor: theme.palette.blue100,
  };

  const ourServicesData: OurServicesProps = {
    our_services_title: prismicCityPage.data.our_services_title,
    our_services: prismicCityPage.data.our_services,
    language,
  };

  const whyQuoteData: QuoteSectionProps = {
    quote_title: prismicCityPage.data.quote_title,
    quote_quote: prismicCityPage.data.quote_quote,
    quote_author: prismicCityPage.data.quote_author,
  };

  const videoData: VideoProps = {
    video_title: prismicCityPage.data.video_title,
    video_title_bottom: prismicCityPage.data.video_title_bottom,
    video_url: prismicCityPage.data.video_url,
  };

  const becomeBatmaidData: BecomeBatmaidProps = {
    become_cleaner_title: prismicCityPage.data.become_cleaner_title,
    become_cleaner_quote: prismicCityPage.data.become_cleaner_quote,
    become_cleaner_quote_author:
      prismicCityPage.data.become_cleaner_quote_author,
    become_cleaner_button_text: prismicCityPage.data.become_cleaner_button_text,
    become_cleaner_button_link: prismicCityPage.data.become_cleaner_button_link,
    lang: pageContext.lang,
    become_cleaner_image: prismicCityPage.data.become_cleaner_image,
  };

  // const cleaningTipsData: CleaningTipsProps = {
  //   cleaning_tips_title: prismicCityPage.data.cleaning_tips_title,
  //   blog_cards: prismicCityPage.data.blog_cards,
  // };

  const offerData: OfferProps = {
    what_do_we_offer_title: prismicCityPage.data.what_do_we_offer_title,
    what_do_we_offer_cards: offerCards,
  };

  const seoData: SeoProps = {
    seo_title: prismicCityPage.data.seo_title,
    seo_description: prismicCityPage.data.seo_description,
    seo_image: prismicCityPage.data.seo_image,
  };

  const partnersData: PartnersProps = {
    partners_logos: prismicCityPage.data.partners_logos,
    partners_title: prismicCityPage.data.partners_title,
  };

  const textSectionData: TextSectionProps = {
    text_section_title: prismicCityPage.data.text_section_title,
    text_section_text: prismicCityPage.data.text_section_text,
  };

  return (
    <Layout
      language={language}
      country={props.pageContext.country}
      origin={ORIGIN.BATMAID}
      isFullWidth
    >
      <UTMCatcher />
      <SEO
        {...seoData}
        path={props.location.pathname}
        title={prismicCityPage.data.title.text}
        breadcrumbs={[
          {
            title: seoData.seo_title?.text || prismicCityPage.data.title.text,
            href: `/${language}/${prismicCityPage.data.slug.text.trim()}`,
          },
        ]}
      />
      <HeroSection {...heroData} isCityPage />
      <Partners {...partnersData} noPaddingBottom />
      <HowItWorks
        {...howItWorksData}
        iconsSize={desktop || desktopLarge ? 96 : 64}
      />
      <Offer {...offerData} />
      <OurServices {...ourServicesData} hideTilesWithoutLink />
      <CitiesServices {...enjoyServiceData} />
      <QuoteSection {...whyQuoteData} />
      <VideoSection {...videoData} />
      <BecomeBatmaidSection {...becomeBatmaidData} withoutImage />
      <TextSection {...textSectionData} />
      {/* <CleaningTips {...cleaningTipsData} /> */}
    </Layout>
  );
};

export default withPrismicPreview(CityPage);

export const query = graphql`
  query CityPageQuery($id: String) {
    prismicCityPage(id: { eq: $id }) {
      _previewable
      lang
      data {
        slug {
          text
        }
        title {
          text
        }
        # ==== HERO SECTIONS ====
        hero_breadcrumb_text {
          text
        }
        hero_image_mobile {
          gatsbyImageData
        }

        body {
          ... on PrismicCityPageDataBodyLandingHero {
            primary {
              hero_type
              hero_section_title {
                text
              }
              hero_navigation_text {
                text
              }
              hero_section_description {
                text
              }
              hero_background_img {
                gatsbyImageData(width: 1920, imgixParams: { q: 90 })
              }
              button_text {
                text
              }
              button_link {
                text
              }
            }

            items {
              hero_description_image {
                gatsbyImageData
              }
              list_item {
                text
              }
            }
          }
        }

        # ==== SEO ====
        seo_title {
          text
        }
        seo_description {
          text
        }
        seo_image {
          gatsbyImageData(width: 1200)
        }
        # ==== EMAIL TEXTS (DEPRECATED) ====
        email_submit {
          text
        }
        email_placeholder {
          text
        }

        # ==== BOOKING FORM TEXTS ====
        booking_form_submit {
          text
        }
        booking_form_placeholder {
          text
        }

        # ==== CITIES SECTION ====
        enjoy_service_title {
          text
        }
        enjoy_service_cities {
          image_caption {
            text
          }
          city_image {
            alt
            gatsbyImageData(
              layout: CONSTRAINED
              width: 334
              imgixParams: { q: 95, fit: "crop", crop: "faces" }
              placeholderImgixParams: { q: 95, fit: "crop", crop: "faces" }
              sizes: "(max-width: 360px) 135px, (max-width: 768px) 335px, (max-width: 991px) 148px, 168px"
              placeholder: BLURRED
            )
          }
          service_link {
            slug
          }
        }
        # ==== HOW IT WORKS SECTION ====
        how_it_works_title {
          text
        }
        how_it_works_title_mobile {
          text
        }
        how_it_works_step_one {
          text
        }
        how_it_works_step_one_icon
        how_it_works_step_two {
          text
        }
        how_it_works_step_two_icon
        how_it_works_step_three {
          text
        }
        how_it_works_step_three_icon
        # ==== OURS SERVICES SECTION ====
        our_services_title {
          text
        }
        our_services {
          service_image {
            gatsbyImageData(
              layout: CONSTRAINED
              width: 334
              imgixParams: { q: 95, fit: "crop", crop: "faces" }
              sizes: "(max-width: 360px) 132px, (max-width: 767px) 336px, (max-width: 991px) 140px, 184px"
              placeholder: BLURRED
            )
          }
          service_title {
            text
          }
          service_link {
            slug
          }
        }
        # ==== QUOTE SECTION ====
        quote_title {
          text
        }
        quote_quote {
          text
        }
        quote_author {
          text
        }
        # ==== VIDEO SECTION ====
        video_title {
          text
        }
        video_url {
          url
        }
        video_title_bottom {
          text
        }
        # ==== BOOK CLEANING SECTION ====
        ready_to_book_title {
          text
        }
        ready_to_book_list {
          list_item {
            text
          }
        }
        ready_to_book_list_mobile {
          list_item {
            text
          }
        }
        ready_to_book_image {
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            width: 688
          )
        }
        # ==== BECOME BATMAID SECTION ====
        become_cleaner_title {
          text
        }
        become_cleaner_quote {
          text
        }
        become_cleaner_quote_author {
          text
        }
        become_cleaner_button_text {
          text
        }
        become_cleaner_button_link {
          text
        }
        become_cleaner_image {
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: { q: 95 }
            placeholder: BLURRED
          )
        }
        # ==== TEXT SECTION ====
        text_section_text {
          text
        }
        text_section_title {
          text
        }
        # ==== CLEANING TIPS SECTION ====
        cleaning_tips_title {
          text
        }
        blog_cards {
          blog_card_title {
            text
          }
          blog_card_background_photo {
            gatsbyImageData
          }
          blog_card_hashtags {
            text
          }
          blog_card_link {
            text
          }
        }
        # ==== PARTNERS SECTION ====
        partners_title {
          text
        }
        partners_logos {
          partner_logo {
            alt
            gatsbyImageData(
              layout: CONSTRAINED
              imgixParams: { q: 100 }
              sizes: "(max-width: 360px) 85px, 144px"
              placeholder: BLURRED
            )
          }
          partner_link {
            url
            target
          }
        }
        # ==== WHAT DO WE OFFER SECTION ====
        what_do_we_offer_title {
          text
        }
        what_do_we_offer_cards {
          what_do_we_offer_card_image {
            gatsbyImageData(
              layout: CONSTRAINED
              width: 334
              imgixParams: { q: 95, fit: "crop", crop: "faces" }
              sizes: "(max-width: 360px) 280px, (max-width: 767px) 687px, (max-width: 991px) 312px, 232px"
              placeholder: BLURRED
            )
          }
          what_do_we_offer_card_image_mobile {
            gatsbyImageData(
              layout: CONSTRAINED
              imgixParams: { q: 95, minH: 191, fit: "crop", crop: "faces" }
              sizes: "(max-width: 360px) 280px, (max-width: 767px) 687px, (max-width: 991px) 312px, 232px"
              placeholder: BLURRED
            )
          }
          what_do_we_offer_card_title {
            text
          }
          what_do_we_offer_card_list_element__1 {
            text
          }
          what_do_we_offer_card_list_element__2 {
            text
          }
          what_do_we_offer_card_list_element__3 {
            text
          }
          what_do_we_offer_card_list_element__4 {
            text
          }
          what_do_we_offer_card_list_element__5 {
            text
          }
        }
      }
    }
  }
`;
